import { useEvents } from '@/api/hooks';
import { EventFormModal } from '@/components/events/EventFormModal';
import { EVENT_TYPES } from '@/components/events/EventFormModal';
import { EventListItem } from '@/components/events/EventListItem';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { PageHeader } from '@/components/ui/PageHeader';
import { ListPagination } from '@/components/ui/ListPagination';
import { Select } from '@/components/ui/Select';
import { usePropertyScope } from '@/contexts/PropertyContext';
import { useLocalePrefs } from '@/contexts/LocaleContext';
import { eventTypeLabel } from '@/lib/format';
import { ClipboardList, Plus, Scale } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export function EventsPage() {
  const { propertyId } = usePropertyScope();
  useLocalePrefs();
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useEvents({
    property_id: propertyId ?? undefined,
    event_type: typeFilter || undefined,
    page,
  });

  const total = data?.meta.total ?? 0;
  const lastPage = data?.meta.last_page ?? 1;

  return (
    <div>
      <PageHeader
        title="Eventos"
        subtitle={
          total > 0
            ? `${total} registro(s) de manejo`
            : 'Timeline de manejo do rebanho'
        }
        actions={
          <div className="flex flex-wrap gap-2">
            {propertyId ? (
              <Link
                to="/pesagem-lote"
                className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-manejai-forest/20 bg-white px-4 text-sm font-medium text-manejai-forest hover:bg-manejai-sand/50"
              >
                <Scale className="h-4 w-4" />
                Registro em lote
              </Link>
            ) : (
              <Button variant="outline" disabled>
                <Scale className="h-4 w-4" />
                Registro em lote
              </Button>
            )}
            <Button onClick={() => setOpen(true)} disabled={!propertyId}>
              <Plus className="h-4 w-4" />
              Registrar evento
            </Button>
          </div>
        }
      />

      {!propertyId ? (
        <Card>
          <CardBody className="py-10 text-center text-manejai-bark/60">
            Selecione uma fazenda ativa no topo.
          </CardBody>
        </Card>
      ) : (
        <>
          <div className="mb-4 max-w-xs">
            <Select
              label="Filtrar por tipo"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: '', label: 'Todos os tipos' },
                ...EVENT_TYPES.map((item) => ({
                  value: item.value,
                  label: eventTypeLabel(item.value),
                })),
              ]}
            />
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 rounded-xl bg-manejai-sand/40 animate-pulse" />
              ))}
            </div>
          ) : (data?.events.length ?? 0) === 0 ? (
            <Card>
              <CardBody className="py-16 text-center">
                <ClipboardList className="h-10 w-10 mx-auto text-manejai-sage mb-3" />
                <p className="text-manejai-bark/70">
                  {typeFilter ? 'Nenhum evento deste tipo.' : 'Nenhum evento registrado.'}
                </p>
              </CardBody>
            </Card>
          ) : (
            <Card>
              <ul className="divide-y divide-manejai-sand/60">
                {data?.events.map((ev) => (
                  <EventListItem key={ev.event_id} event={ev} />
                ))}
              </ul>

              <ListPagination
                page={page}
                lastPage={lastPage}
                summary={`${total} registro(s)`}
                onPageChange={setPage}
              />
            </Card>
          )}
        </>
      )}

      {propertyId && (
        <EventFormModal open={open} onClose={() => setOpen(false)} propertyId={propertyId} />
      )}
    </div>
  );
}
